import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CallHistoryTable = ({ calls, onPlayRecording, onEditNotes, onViewDetails }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'dateTime', direction: 'desc' });
  const [expandedRows, setExpandedRows] = useState(new Set());

  const sortedCalls = useMemo(() => {
    const sortableItems = [...calls];
    if (sortConfig?.key) {
      sortableItems?.sort((a, b) => {
        if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [calls, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig?.key === key && prevConfig?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleRowExpansion = (callId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(callId)) {
      newExpanded?.delete(callId);
    } else {
      newExpanded?.add(callId);
    }
    setExpandedRows(newExpanded);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const getDispositionColor = (disposition) => {
    const colors = {
      'Başarılı': 'bg-green-100 text-green-800',
      'Meşgul': 'bg-yellow-100 text-yellow-800',
      'Cevapsız': 'bg-red-100 text-red-800',
      'Geri Arama': 'bg-blue-100 text-blue-800',
      'Yanlış Numara': 'bg-gray-100 text-gray-800'
    };
    return colors?.[disposition] || 'bg-gray-100 text-gray-800';
  };

  const getCallTypeIcon = (type) => {
    return type === 'Gelen' ? 'ArrowDown' : 'ArrowUp';
  };

  const getCallTypeColor = (type) => {
    return type === 'Gelen' ? 'text-blue-600' : 'text-green-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-subtle border border-slate-200 overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort('dateTime')}
                  className="flex items-center space-x-1 hover:text-slate-700"
                >
                  <span>Tarih/Saat</span>
                  <Icon 
                    name={sortConfig?.key === 'dateTime' ? 
                      (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'
                    } 
                    size={14} 
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort('customerName')}
                  className="flex items-center space-x-1 hover:text-slate-700"
                >
                  <span>Müşteri</span>
                  <Icon 
                    name={sortConfig?.key === 'customerName' ? 
                      (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'
                    } 
                    size={14} 
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Telefon
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Tür
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort('duration')}
                  className="flex items-center space-x-1 hover:text-slate-700"
                >
                  <span>Süre</span>
                  <Icon 
                    name={sortConfig?.key === 'duration' ? 
                      (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'
                    } 
                    size={14} 
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Sonuç
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Temsilci
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {sortedCalls?.map((call) => (
              <React.Fragment key={call?.id}>
                <tr className="hover:bg-slate-50 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    <div>
                      <div className="font-medium">{call?.date}</div>
                      <div className="text-slate-500">{call?.time}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-900">{call?.customerName}</div>
                    {call?.company && (
                      <div className="text-sm text-slate-500">{call?.company}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 font-mono">
                    {call?.phoneNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`flex items-center space-x-1 ${getCallTypeColor(call?.type)}`}>
                      <Icon name={getCallTypeIcon(call?.type)} size={16} />
                      <span className="text-sm font-medium">{call?.type}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 font-mono">
                    {formatDuration(call?.duration)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDispositionColor(call?.disposition)}`}>
                      {call?.disposition}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {call?.agentName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleRowExpansion(call?.id)}
                        iconName={expandedRows?.has(call?.id) ? "ChevronUp" : "ChevronDown"}
                      />
                      {call?.hasRecording && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onPlayRecording(call?.id)}
                          iconName="Play"
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditNotes(call?.id)}
                        iconName="Edit"
                      />
                    </div>
                  </td>
                </tr>
                {expandedRows?.has(call?.id) && (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 bg-slate-50">
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-medium text-slate-900 mb-2">Arama Detayları</h4>
                            <div className="space-y-1 text-sm text-slate-600">
                              <div>Arama ID: {call?.callId}</div>
                              <div>Bekleme Süresi: {call?.waitTime}s</div>
                              <div>Transfer Sayısı: {call?.transferCount}</div>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-slate-900 mb-2">Notlar</h4>
                            <p className="text-sm text-slate-600">{call?.notes}</p>
                          </div>
                        </div>
                        {call?.customerHistory && call?.customerHistory?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-900 mb-2">Müşteri Geçmişi</h4>
                            <div className="space-y-2">
                              {call?.customerHistory?.slice(0, 3)?.map((history, index) => (
                                <div key={index} className="flex items-center space-x-3 text-sm">
                                  <span className="text-slate-500">{history?.date}</span>
                                  <span className="text-slate-900">{history?.summary}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden">
        <div className="divide-y divide-slate-200">
          {sortedCalls?.map((call) => (
            <div key={call?.id} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name={getCallTypeIcon(call?.type)} size={16} className={getCallTypeColor(call?.type)} />
                    <span className="font-medium text-slate-900">{call?.customerName}</span>
                  </div>
                  <div className="text-sm text-slate-600 font-mono">{call?.phoneNumber}</div>
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDispositionColor(call?.disposition)}`}>
                  {call?.disposition}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                <div>
                  <span className="text-slate-500">Tarih: </span>
                  <span className="text-slate-900">{call?.date}</span>
                </div>
                <div>
                  <span className="text-slate-500">Süre: </span>
                  <span className="text-slate-900 font-mono">{formatDuration(call?.duration)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Saat: </span>
                  <span className="text-slate-900">{call?.time}</span>
                </div>
                <div>
                  <span className="text-slate-500">Temsilci: </span>
                  <span className="text-slate-900">{call?.agentName}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleRowExpansion(call?.id)}
                  iconName={expandedRows?.has(call?.id) ? "ChevronUp" : "ChevronDown"}
                >
                  {expandedRows?.has(call?.id) ? 'Gizle' : 'Detaylar'}
                </Button>
                <div className="flex items-center space-x-2">
                  {call?.hasRecording && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onPlayRecording(call?.id)}
                      iconName="Play"
                    />
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditNotes(call?.id)}
                    iconName="Edit"
                  />
                </div>
              </div>

              {expandedRows?.has(call?.id) && (
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Arama Detayları</h4>
                    <div className="space-y-1 text-sm text-slate-600">
                      <div>Arama ID: {call?.callId}</div>
                      <div>Bekleme Süresi: {call?.waitTime}s</div>
                      <div>Transfer Sayısı: {call?.transferCount}</div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Notlar</h4>
                    <p className="text-sm text-slate-600">{call?.notes}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CallHistoryTable;